import { GameState, SpaceType, PropertySpace } from '../shared/types.js';
import { BOARD_SPACES } from '../shared/boardSpaces.js';
import { $, formatMoney } from './utils.js';
import { PROPERTY_COLOR_HEX } from '../shared/constants.js';

export function initUIPanel(): void {
  // Panel is updated reactively via updateUI
}

// Diff cache: per-player fingerprint of the inputs that affect their card.
// If the fingerprint hasn't changed, we skip rebuilding that card entirely.
const cardCache = new Map<string, string>();
let lastStatusMessage: string | null = null;
let lastPlayerOrder: string | null = null;

function fingerprintPlayer(
  player: GameState['players'][number],
  isCurrent: boolean,
  isMe: boolean,
  isDisconnected: boolean,
  state: GameState,
): string {
  const propStatus = player.properties.map(pos => {
    const owned = state.properties.find(p => p.position === pos);
    return owned ? `${pos}:${owned.isMortgaged ? 'M' : owned.houses}` : `${pos}:?`;
  }).join(',');
  return [
    player.id, player.money, player.inJail, player.isBankrupt,
    player.getOutOfJailCards, isCurrent, isMe, isDisconnected, player.isBot, propStatus,
  ].join('|');
}

export function updateUI(state: GameState, myPlayerId: string): void {
  // Status message — only write when it changed.
  if (state.message !== lastStatusMessage) {
    lastStatusMessage = state.message;
    $('#status-message').textContent = state.message;
  }

  const panel = $('#player-info-panel');
  const orderKey = state.players.map(p => p.id).join(',');

  // If players have been added/removed, blow away the panel and start fresh —
  // anything subtler than that we handle per-card below.
  if (orderKey !== lastPlayerOrder) {
    panel.innerHTML = '';
    cardCache.clear();
    lastPlayerOrder = orderKey;
  }

  for (let i = 0; i < state.players.length; i++) {
    const player = state.players[i];
    const isCurrent = i === state.currentPlayerIndex;
    const isMe = player.id === myPlayerId;
    const isDisconnected = state.disconnectedPlayerIds?.includes(player.id) ?? false;
    const fp = fingerprintPlayer(player, isCurrent, isMe, isDisconnected, state);

    if (cardCache.get(player.id) === fp) continue;
    cardCache.set(player.id, fp);

    const card = renderPlayerCard(player, isCurrent, isMe, isDisconnected, state);
    const existing = panel.children[i] as HTMLElement | undefined;
    if (existing) {
      panel.replaceChild(card, existing);
    } else {
      panel.appendChild(card);
    }
  }
}

function renderPlayerCard(
  player: GameState['players'][number],
  isCurrent: boolean,
  isMe: boolean,
  isDisconnected: boolean,
  state: GameState,
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'player-card';
  card.style.setProperty('--player-color', player.color);

  if (isCurrent) card.classList.add('current');
  if (player.isBankrupt) card.classList.add('bankrupt');
  if (isDisconnected) card.classList.add('disconnected');

  const header = document.createElement('div');
  header.className = 'player-card-header';

  const avatarEl = document.createElement('span');
  avatarEl.className = 'player-card-avatar';
  avatarEl.textContent = player.avatar || '?';

  const nameEl = document.createElement('span');
  nameEl.className = 'player-card-name';
  let nameText = player.name;
  if (player.isBot) nameText += ' 🤖';
  if (isMe) nameText += ' (You)';
  if (player.inJail) nameText += ' [JAIL]';
  if (player.isBankrupt) nameText += ' [BANKRUPT]';
  if (isDisconnected) nameText += ' [DC]';
  nameEl.textContent = nameText;

  const moneyEl = document.createElement('span');
  moneyEl.className = 'player-card-money';
  moneyEl.textContent = player.isBankrupt ? 'BANKRUPT' : formatMoney(player.money);

  const nameWrap = document.createElement('span');
  nameWrap.className = 'player-card-identity';
  nameWrap.appendChild(avatarEl);
  nameWrap.appendChild(nameEl);

  header.appendChild(nameWrap);
  header.appendChild(moneyEl);
  card.appendChild(header);

  if (player.properties.length > 0) {
    const propsEl = document.createElement('div');
    propsEl.className = 'player-card-properties';

    for (const pos of player.properties) {
      const space = BOARD_SPACES[pos];
      if (!space) continue;
      const dot = document.createElement('span');
      dot.className = 'property-dot';
      dot.title = space.name;

      if (space.type === SpaceType.PROPERTY) {
        const color = PROPERTY_COLOR_HEX[(space as PropertySpace).color] || '#888';
        dot.style.backgroundColor = color;
      } else if (space.type === SpaceType.RAILROAD) {
        dot.style.backgroundColor = '#333';
      } else if (space.type === SpaceType.UTILITY) {
        dot.style.backgroundColor = '#888';
      }

      const owned = state.properties.find(p => p.position === pos);
      if (owned && owned.isMortgaged) {
        dot.title += ' (Mortgaged)';
        dot.classList.add('mortgaged-dot');
      } else if (owned && owned.houses > 0) {
        dot.title += owned.houses === 5
          ? ' (Hotel)'
          : ` (${owned.houses} house${owned.houses > 1 ? 's' : ''})`;
      }

      propsEl.appendChild(dot);
    }

    card.appendChild(propsEl);
  }

  if (player.getOutOfJailCards > 0) {
    const jailEl = document.createElement('div');
    jailEl.className = 'player-card-properties';
    jailEl.textContent = `Get Out of Jail cards: ${player.getOutOfJailCards}`;
    card.appendChild(jailEl);
  }

  return card;
}
